import clsx from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'warning';
type Size = 'md' | 'sm';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-accent-fg hover:opacity-90',
  secondary: 'bg-surface-raised text-content border border-border-token hover:bg-surface-sunken',
  ghost: 'bg-transparent text-content hover:bg-surface-sunken',
  danger: 'bg-red-600 text-white hover:bg-red-700',
  // For actions that mutate but aren't destructive (bulk re-run, retry) —
  // distinct from `danger` so "this deletes/cancels something" and "this
  // re-runs a calculation" don't share one visual alarm level. Token-backed
  // (bg-warning/text-warning-fg), not a literal Tailwind class: see
  // src/lib/theme.ts and src/index.css. Uses amber-700, not amber-600 —
  // amber-600 on white text is 3.19:1 and fails WCAG AA; amber-700 is
  // 5.02:1 and passes. Asserted in src/test/contrast.test.ts so this
  // doesn't rest on hand arithmetic.
  warning: 'bg-warning text-warning-fg hover:opacity-90',
};

const SIZES: Record<Size, string> = {
  md: 'px-4 py-2.5 text-sm',
  sm: 'px-3 py-2 text-sm',
};

export function Button({ variant = 'primary', size = 'md', className, children, type = 'button', ...rest }: ButtonProps) {
  return (
    <button
      type={type}
      className={clsx(
        // `tap-target` keeps every button at the 44px floor even at size="sm",
        // and carries the inline-flex centring with it — see the comment on
        // the plugin in tailwind.config.js for why the two are one token.
        'tap-target gap-2 rounded-md font-semibold transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
